import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaArrowLeft, FaCalendarAlt, FaClock, FaUserFriends, FaTag } from 'react-icons/fa';
import { getLivestreamById, validateAccessCode, updateViewCount } from '../../../http/Actions/LivestreamAction';

const ReproductorLivestream = () => {
  const { idLivestream } = useParams();

  const [livestream, setLivestream] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [validatingAccess, setValidatingAccess] = useState(false);
  const playerRef = useRef(null);
  const [viewUpdated, setViewUpdated] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    let mounted = true;
    
    const fetchLivestream = async () => {
      setLoading(true);
      try {
        const response = await getLivestreamById(idLivestream, (data) => {
          if (mounted) {
            setLivestream(data);
            setHasAccess(data.userHasAccess || false);
          }
        }, setError, token);
        
        if (mounted) {
          setLoading(false);
        }
      } catch (err) {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchLivestream();

    return () => {
      mounted = false;
      if (playerRef.current) {

        playerRef.current = null;
      }
    };
  }, [idLivestream]);

  // Initialize YouTube player
  useEffect(() => {
    if (livestream && hasAccess && !playerRef.current) {

      if (!window.YT) {
        const tag = document.createElement('script');
        tag.src = "https://www.youtube.com/iframe_api";
        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      }

      const videoId = getYoutubeVideoId(livestream.videoURL);
      
      const initPlayer = () => {
        playerRef.current = new window.YT.Player('youtube-player', {
          height: '100%',
          width: '100%',
          videoId: videoId,
          playerVars: {
            'autoplay': 0,
            'controls': 1,
            'rel': 0,
            'fs': 1,
            'modestbranding': 1
          },
          events: {
            'onStateChange': onPlayerStateChange
          }
        });
      };

      if (window.YT && window.YT.Player) {
        initPlayer();
      } else {
        window.onYouTubeIframeAPIReady = initPlayer;
      }

      const onPlayerStateChange = (event) => {
        if (event.data === window.YT.PlayerState.PLAYING && !viewUpdated) {
          updateViewCount(idLivestream, setError)
            .then(() => setViewUpdated(true))
            .catch(err => console.error("Error updating view count:", err));
        }
      };
    }
  }, [livestream, hasAccess, idLivestream, viewUpdated]);

  const handleAccessCodeSubmit = async (e) => {
    e.preventDefault();
    if (!accessCode.trim()) return;
    
    setValidatingAccess(true);
    try {
      const isValid = await validateAccessCode(idLivestream, accessCode, (isValid) => {
        setHasAccess(isValid);
      }, setError);
      
      if (!isValid) {
        setError("Código de acceso inválido");
      }
    } catch (err) {
    } finally {
      setValidatingAccess(false);
    }
  };

  const getYoutubeVideoId = (url) => {
    if (!url) return null;
    
    if (url.includes('youtube.com/live/')) {
      const liveMatch = url.match(/youtube\.com\/live\/([^?&#]+)/);
      if (liveMatch && liveMatch[1]) {
        return liveMatch[1];
      }
    }
    
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    
    if (match && match[2].length === 11) {
      return match[2];
    }
    
    return null;
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-b from-gray-900 to-black text-white min-h-screen flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center">
          <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-xl font-semibold">Cargando...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gradient-to-b from-gray-900 to-black text-white min-h-screen flex items-center justify-center p-8">
        <div className="bg-red-900/50 p-6 rounded-lg max-w-md text-center backdrop-blur-sm border border-red-700">
          <h2 className="text-2xl font-bold mb-4">¡Algo salió mal!</h2>
          <p className="text-lg">{error}</p>
          <Link to="/" className="mt-6 inline-block px-6 py-2 bg-purple-700 hover:bg-purple-600 rounded-full transition-all duration-300">
            Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  if (!livestream) {
    return null;
  }

  if (!hasAccess) {
    return (
      <div className="bg-gradient-to-b from-gray-900 to-black text-white min-h-screen flex items-center justify-center p-8">
        <div className="bg-gray-800/80 p-8 rounded-xl max-w-md text-center backdrop-blur-sm border border-gray-700 w-full">
          <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
            {livestream.titulo}
          </h2>
          <div className="h-1 w-24 bg-gradient-to-r from-purple-500 to-pink-500 my-3 mx-auto rounded-full"></div>
          
          <p className="mb-6 text-gray-300">Necesitas un código de acceso para ver este livestream.</p>
          
          <form onSubmit={handleAccessCodeSubmit} className="mb-6">
            <input
              type="text"
              className="w-full p-3 rounded-lg bg-gray-700 text-white border border-gray-600 focus:border-purple-500 focus:outline-none mb-4"
              placeholder="Ingresa tu código de acceso"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
            />
            <button
              type="submit"
              disabled={validatingAccess}
              className="w-full py-3 px-6 bg-purple-700 hover:bg-purple-600 rounded-lg transition-all duration-300 font-semibold disabled:opacity-50"
            >
              {validatingAccess ? 'Validando...' : 'Acceder al Livestream'}
            </button>
          </form>
          
          <Link to="/" className="text-sm text-purple-400 hover:text-purple-300 flex items-center justify-center gap-2">
            <FaArrowLeft /> Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-gray-900 to-black text-white min-h-screen p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="video-container rounded-xl overflow-hidden shadow-2xl mb-8" style={{ position: 'relative', paddingTop: '56.25%', userSelect: 'none' }}>
          <div id="youtube-player" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}></div>
        </div>
        
        <div className="container max-w-5xl mx-auto">
          <div className="bg-gray-800/80 backdrop-blur-sm p-6 rounded-xl shadow-lg transition-all duration-300 hover:bg-gray-800" style={{ userSelect: 'none' }}>
            <div className="mb-6">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
                {livestream.titulo || 'Sin título'}
              </h1>
              <div className="h-1 w-24 bg-gradient-to-r from-purple-500 to-pink-500 my-3 rounded-full"></div>
            </div>
            
            <p className="text-gray-200 text-lg leading-relaxed mb-6">{livestream.descripcion || 'Sin descripción'}</p>
            
            <div className="flex flex-wrap gap-4 text-sm text-gray-300 mb-6">
              <div className="flex items-center">
                <FaClock className="mr-2 text-purple-400" />
                <span>
                  {livestream.estado === 'En Vivo' 
                    ? 'EN VIVO AHORA' 
                    : livestream.estado === 'Próximo' 
                      ? `Comienza el ${new Date(livestream.fecha).toLocaleDateString()} a las ${new Date(livestream.fecha).toLocaleTimeString()}` 
                      : `Finalizado el ${new Date(livestream.fecha).toLocaleDateString()}`
                  }
                </span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="mr-2 text-purple-400" />
                <span>Fecha: {new Date(livestream.fecha).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center">
                <FaUserFriends className="mr-2 text-purple-400" />
                <span>Vistas: {livestream.vistas}</span>
              </div>
            </div>
            
            {/* Comediantes */}
            {livestream.comediantes && livestream.comediantes.length > 0 && (
              <div className="mb-6">
                <h3 className="text-xl font-semibold mb-3">Comediantes</h3>
                <div className="flex flex-wrap gap-4">
                  {livestream.comediantes.map(comediante => (
                    <div 
                      key={comediante.comedianteId} 
                      className="flex items-center bg-gray-700/50 rounded-full px-4 py-2"
                    >
                      {comediante.imagen && (
                        <img 
                          src={comediante.imagen} 
                          alt={comediante.nombre} 
                          className="w-8 h-8 rounded-full mr-2 object-cover"
                        />
                      )}
                      <span>{comediante.nombre}</span>
                      {comediante.esPrincipal && (
                        <span className="ml-2 text-xs bg-purple-700 rounded-full px-2 py-0.5">Principal</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Tags */}
            {livestream.tags && livestream.tags.length > 0 && (
              <div>
                <h3 className="text-xl font-semibold mb-3">Etiquetas</h3>
                <div className="flex flex-wrap gap-2">
                  {livestream.tags.map(tag => (
                    <div 
                      key={tag.tagId} 
                      className="flex items-center rounded-full px-3 py-1"
                      style={{ backgroundColor: tag.colorHex || '#6D28D9' }}
                    >
                      <FaTag className="mr-1 text-sm" />
                      <span className="text-sm">{tag.nombre}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReproductorLivestream;